import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, Fragment } from "react";
import { Reveal, Eyebrow } from "../components/primitives";
import CTABand from "../components/CTABand";
import { IMAGES, CONTACT } from "../data/content";

const CHAPTERS = [
  {
    n: "01",
    title: "Matcha and pizza",
    body:
      "Yes, that's matcha and pizza. At the same table. I've stopped explaining it. But it's a fair summary of how I think. I want the beautiful thing and the honest thing at once, and I've never accepted that you have to pick one.",
  },
  {
    n: "02",
    title: "You know the call",
    body:
      "If you run a fashion brand, you know the call. Explaining what a drop is. Explaining why it can't just be the product on a white background. Explaining that your customer buys with her eyes, not a discount code. And realising, around minute twenty, that the person on the other end is waiting for you to finish so they can talk about click-through rates. I spent ten years inside agencies, watching that call from the other side of the table.",
  },
  {
    n: "03",
    title: "A room where you don't explain",
    body:
      "So I built Connect.Co to be the room where you don't have to explain. Fashion, beauty and luxury, deliberately, not one vertical out of ten. And at the hardest part, which is the beginning. Not brands that are already big, but the ones with a beautiful product and no engine behind it. That's the segment nobody was building for. So that's the one we built.",
  },
  {
    n: "04",
    title: "One clear belief",
    body:
      "Ten years of knowing this industry. One clear belief: fashion deserves marketing built for fashion.",
  },
];

function FounderImage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <div ref={ref} className="relative overflow-hidden rounded-sm bg-taupe aspect-[4/5]">
      <motion.img
        src={IMAGES.founder}
        alt="Vaishnavi Modi, Founder of Connect.Co"
        className="w-full h-full object-cover"
        style={{ y, scale: 1.1 }}
      />
    </div>
  );
}

export default function About() {
  return (
    <>
      <section className="bg-ivory pt-36 md:pt-48" data-testid="about-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <Reveal><Eyebrow>The Founder</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-serif font-light text-charcoal leading-[0.98] tracking-tight text-6xl md:text-8xl lg:text-[8vw]">
              Vaishnavi Modi
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-5 md:sticky md:top-28">
            <Reveal><FounderImage /></Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 font-sans text-xs uppercase tracking-[0.22em] text-gold">
                {CONTACT.cities.join(" · ")}
              </p>
            </Reveal>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <div className="space-y-16">
              {CHAPTERS.map((c, i) => (
                <Fragment key={c.n}>
                  <Reveal>
                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                      <div className="sm:col-span-1">
                        <span className="font-serif text-3xl text-gold">{c.n}</span>
                      </div>
                      <div className="sm:col-span-5">
                        <h3 className="font-serif text-2xl md:text-3xl text-charcoal font-normal mb-4">{c.title}</h3>
                        <p className="font-sans text-base md:text-lg text-charcoal/70 leading-[1.75] font-light">
                          {c.body}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                  {i === 1 && (
                    <Reveal>
                      <div className="relative overflow-hidden rounded-sm bg-taupe aspect-[4/5]">
                        <img
                          src={IMAGES.founder2}
                          alt="Vaishnavi Modi at the table"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Reveal>
                  )}
                </Fragment>
              ))}

              <Reveal>
                <p className="font-serif italic text-2xl md:text-3xl text-charcoal pt-6 border-t border-taupe">
                  Vaishnavi Modi, Founder, Connect.Co
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTABand eyebrow="Work With Us" title="Let's build a brand that lasts." variant="taupe" />
    </>
  );
}
